import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatLessonAmount } from '../../application/formatLessonPrice';
import type { LessonPrice } from '../../application/types';
import './MentorCard.scss';

export type MentorCardCategory = {
  id: string | number;
  title: string;
};

export type MentorCardProps = {
  name: string;
  headline?: string | null;
  imageUrl?: string | null;
  categories?: MentorCardCategory[];
  price?: LessonPrice | null;
  to?: string;
};

function nameLines(name: string): string[] {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return [name.trim() || '—'];
  return [parts[0], parts.slice(1).join(' ')];
}

export function MentorCard({
  name,
  headline,
  imageUrl,
  categories = [],
  price = null,
  to,
}: MentorCardProps) {
  const { t, i18n } = useTranslation();
  const priceLabel = price
    ? t('mentor-profile.price_line', {
        amount: formatLessonAmount(price, i18n.language),
        duration: price.durationMinutes,
      })
    : t('mentor-profile.price_not_set');
  const lines = nameLines(name);
  const body = (
    <>
      {imageUrl ? (
        <img className="mentor-card__photo" src={imageUrl} alt="" />
      ) : (
        <span className="mentor-card__photo mentor-card__photo--empty" aria-hidden />
      )}
      <span className="mentor-card__body">
        <span className="mentor-card__name">
          {lines.map((line, index) => (
            <span key={index} className="mentor-card__name-line">
              {line}
            </span>
          ))}
        </span>
        {headline ? <span className="mentor-card__headline">{headline}</span> : null}
        {categories.length > 0 ? (
          <span className="mentor-card__categories">
            {categories.map((category) => (
              <span key={category.id} className="mentor-card__category">
                {category.title}
              </span>
            ))}
          </span>
        ) : null}
        <span className="mentor-card__price">
          <span className="mentor-card__price-label">{t('mentors.lessonFrom')}</span>
          <span className="mentor-card__price-value">{priceLabel}</span>
        </span>
      </span>
    </>
  );

  if (!to) return <article className="mentor-card">{body}</article>;

  return (
    <Link className="mentor-card" to={to} aria-label={name}>
      {body}
    </Link>
  );
}

export default MentorCard;
